import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Send, Loader2 } from "lucide-react";
import { aiApi } from "../lib/api";
import { normalizeDecisionMakerCode } from "./PersonaUtils";
import { normalizePersonalityStyleCode, normalizeMaturityCode, normalizeInternalPressureCode } from "../lib/persona-personality";

// --- Robot Mascot SVG ---
function RobotMascot({ color = "#877BF2", expression = "happy", size = 48 }: { color?: string; expression?: "happy" | "thinking" | "excited"; size?: number }) {
  const eyeY = expression === "thinking" ? 18 : 16;
  const mouthD = expression === "excited" ? "M 14 26 Q 20 32 26 26" : expression === "thinking" ? "M 16 26 L 24 26" : "M 14 26 Q 20 30 26 26";
  const bounce = expression === "excited" ? "animate-bounce" : expression === "thinking" ? "animate-pulse" : "";

  return (
    <div className={`shrink-0 ${bounce}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width={size} height={size}>
        {/* Antenna */}
        <circle cx="20" cy="4" r="2.5" fill={color} />
        <line x1="20" y1="6.5" x2="20" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round" />
        {/* Head */}
        <rect x="6" y="10" width="28" height="22" rx="8" fill={color} />
        {/* Eyes */}
        <circle cx="14" cy={eyeY} r="3" fill="white" />
        <circle cx="26" cy={eyeY} r="3" fill="white" />
        <circle cx="14.5" cy={eyeY + 0.5} r="1.5" fill="#333" />
        <circle cx="26.5" cy={eyeY + 0.5} r="1.5" fill="#333" />
        {/* Eye sparkle */}
        <circle cx="15.5" cy={eyeY - 0.5} r="0.7" fill="white" />
        <circle cx="27.5" cy={eyeY - 0.5} r="0.7" fill="white" />
        {/* Mouth */}
        <path d={mouthD} stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
        {/* Cheeks */}
        <circle cx="9" cy="22" r="2" fill={color} opacity="0.5" />
        <circle cx="31" cy="22" r="2" fill={color} opacity="0.5" />
        {/* Body hint */}
        <rect x="14" y="32" width="12" height="6" rx="3" fill={color} opacity="0.7" />
      </svg>
    </div>
  );
}

// --- Message Bubble ---
function MessageBubble({ role, content, robotColor }: { role: "assistant" | "user"; content: string; robotColor: string }) {
  const isAssistant = role === "assistant";

  // Remove JSON blocks from display
  const displayContent = content.replace(/```json\n?\{[^}]+\}\n?```/g, "").trim();
  if (!displayContent) return null;

  return (
    <div className={`flex gap-3 ${isAssistant ? "" : "flex-row-reverse"}`}>
      {isAssistant && <RobotMascot color={robotColor} expression="happy" size={36} />}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isAssistant
            ? "bg-white border border-gray-200 text-gray-800"
            : "bg-blue-600 text-white"
        }`}
      >
        {displayContent.split("\n").map((line, i) => {
          if (line.startsWith("**") && line.endsWith("**")) {
            return <p key={i} className="font-bold mt-2 first:mt-0">{line.replace(/\*\*/g, "")}</p>;
          }
          if (line.startsWith("- ")) {
            return <p key={i} className="ml-2">• {line.substring(2)}</p>;
          }
          return line ? <p key={i} className={i > 0 ? "mt-1" : ""}>{line}</p> : <br key={i} />;
        })}
      </div>
    </div>
  );
}

// --- Proposed Item Card ---
function ProposedItemCard({
  data,
  type,
  onValidate,
  onEdit,
}: {
  data: any;
  type: "offer" | "persona" | "criterion" | "context";
  onValidate: () => void;
  onEdit: () => void;
}) {
  const labels: Record<string, string> = {
    offer: "Offre proposée",
    persona: "Persona proposé",
    criterion: "Critère proposé",
    context: "Contexte proposé",
  };
  return (
    <div className="mx-12 my-2 p-4 rounded-xl border-2 border-dashed border-green-300 bg-green-50/50">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-green-600 text-xs font-semibold uppercase tracking-wide">
          {labels[type]}
        </span>
      </div>
      {type === "offer" && (
        <>
          <p className="font-semibold text-sm">{data.name}</p>
          <p className="text-sm text-gray-600 mt-1">{data.description}</p>
        </>
      )}
      {type === "persona" && (
        <>
          <p className="font-semibold text-sm">{data.function || data.csp} ({data.personaType})</p>
          {data.decisionMakerProfile && <p className="text-xs text-gray-500">Profil : {data.decisionMakerProfile}</p>}
          {data.painPoints && <p className="text-xs text-gray-500">Enjeux : {data.painPoints}</p>}
          {data.purchaseCriteria && <p className="text-xs text-gray-500">Critère : {data.purchaseCriteria}</p>}
        </>
      )}
      {type === "criterion" && (
        <>
          <p className="font-semibold text-sm">
            {data.labelFr || data.labelEn}
            <span className="ml-2 text-xs text-gray-500 font-normal">
              [{data.type === "score" ? `Score /${data.maxScore || 10}` : "Oui/Non"}]
            </span>
          </p>
          {data.labelEn && data.labelFr && data.labelEn !== data.labelFr && (
            <p className="text-xs text-gray-400 italic mt-0.5">EN : {data.labelEn}</p>
          )}
          {data.descriptionFr && <p className="text-sm text-gray-600 mt-2">{data.descriptionFr}</p>}
          {data.descriptionEn && data.descriptionEn !== data.descriptionFr && (
            <p className="text-xs text-gray-400 italic mt-1">EN : {data.descriptionEn}</p>
          )}
        </>
      )}
      {type === "context" && (
        <>
          <p className="font-semibold text-sm">{data.nameFr || data.nameEn}</p>
          {data.nameEn && data.nameFr && data.nameEn !== data.nameFr && (
            <p className="text-xs text-gray-400 italic mt-0.5">EN : {data.nameEn}</p>
          )}
          {data.descriptionFr && <p className="text-sm text-gray-600 mt-2">{data.descriptionFr}</p>}
          {data.descriptionEn && data.descriptionEn !== data.descriptionFr && (
            <p className="text-xs text-gray-400 italic mt-1">EN : {data.descriptionEn}</p>
          )}
        </>
      )}
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={onValidate} className="bg-green-600 hover:bg-green-700">
          Valider
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit}>
          Ajuster
        </Button>
      </div>
    </div>
  );
}

// --- Main AiChat Component ---
export interface AiChatProps {
  agentCode: "offer_creator" | "persona_creator" | "criteria_creator" | "context_creator";
  robotColor: string;
  bgColor: string;
  context: {
    companyDescription?: string;
    offers?: string;
    simulatorName?: string;
    simulatorDescription?: string;
    existingCriteria?: string;
    existingContexts?: string;
    language?: string;
  };
  /** Peut être asynchrone : une erreur remonte ici et est montrée dans le fil. */
  onItemCreated: (type: "offer" | "persona" | "criterion" | "context", data: any) => void | Promise<void>;
  onComplete: () => void;
  title: string;
  subtitle: string;
  /**
   * Si false, le composant attend avant d'envoyer le message d'init à l'agent.
   * Permet aux callers d'éviter une race condition quand `context` est rempli
   * de façon asynchrone (ex: wizard qui charge `companyDescription` au open).
   * Défaut : true (init immédiat, comportement legacy).
   */
  ready?: boolean;
}

export function AiChat({ agentCode, robotColor, bgColor, context, onItemCreated, onComplete, title, subtitle, ready = true }: AiChatProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Array<{ role: "assistant" | "user"; content: string }>>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposedItem, setProposedItem] = useState<any>(null);
  const [proposedType, setProposedType] = useState<"offer" | "persona" | "criterion" | "context">("offer");
  const [robotExpression, setRobotExpression] = useState<"happy" | "thinking" | "excited">("happy");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, proposedItem]);

  // Start conversation — n'init qu'une fois et seulement si ready=true.
  // Ready peut basculer de false à true plus tard (ex: chargement async du
  // companyDescription dans SimulationCaseWizard), on attend cette transition.
  const initSentRef = useRef(false);
  useEffect(() => {
    if (!ready || initSentRef.current) return;
    initSentRef.current = true;
    sendMessage("", true);
  }, [ready]);

  const parseActions = (content: string) => {
    const jsonMatch = content.match(/```json\n?\{([^}]+)\}\n?```/);
    if (!jsonMatch) return null;
    try {
      return JSON.parse(`{${jsonMatch[1]}}`);
    } catch {
      return null;
    }
  };

  const sendMessage = async (userMessage: string, isInit = false) => {
    const newMessages = isInit ? [] : [...messages, { role: "user" as const, content: userMessage }];
    if (!isInit) {
      setMessages(newMessages);
      setInput("");
    }

    setLoading(true);
    setRobotExpression("thinking");

    try {
      const apiMessages = newMessages.map((m) => ({ role: m.role, content: m.content }));
      const result = await aiApi.chat(agentCode, apiMessages, context);
      const assistantMessage = result.message || result.content || "";

      setMessages([...newMessages, { role: "assistant", content: assistantMessage }]);
      setRobotExpression("happy");

      // Check for actions in the response
      const action = parseActions(assistantMessage);
      if (action) {
        if (action.action === "propose_offer") {
          setProposedItem({ name: action.name, description: action.description });
          setProposedType("offer");
          setRobotExpression("excited");
        } else if (action.action === "propose_persona") {
          const { action: _, ...personaData } = action;
          // L'IA renvoie parfois des libellés au lieu des codes attendus par les selects → on normalise.
          if (personaData.decisionMakerProfile) personaData.decisionMakerProfile = normalizeDecisionMakerCode(personaData.decisionMakerProfile);
          if (personaData.personalityStyle) {
            personaData.personalityStyle = normalizePersonalityStyleCode(personaData.personalityStyle);
          }
          // ⚠️ Le mode reste 'variable' — le défaut du produit, quel que soit le chemin de
          // création. Avant, un style proposé par l'IA basculait le persona en 'fixed' : il
          // jouait alors TOUJOURS le même caractère, alors que l'intérêt du mode variable est
          // que le commercial s'entraîne face à des interlocuteurs différents (rotation par
          // (user, persona), couverture complète du catalogue avant répétition).
          // Le client garde la main : il peut passer en mode fixe dans la fiche persona.
          delete personaData.personalityMode;
          delete personaData.fixedPersonalityStyles;
          if (personaData.maturityLevel) personaData.maturityLevel = normalizeMaturityCode(personaData.maturityLevel);
          if (personaData.internalPressure) personaData.internalPressure = normalizeInternalPressureCode(personaData.internalPressure);
          setProposedItem(personaData);
          setProposedType("persona");
          setRobotExpression("excited");
        } else if (action.action === "propose_criterion") {
          const { action: _, ...criterionData } = action;
          setProposedItem(criterionData);
          setProposedType("criterion");
          setRobotExpression("excited");
        } else if (action.action === "propose_context") {
          const { action: _, ...contextData } = action;
          setProposedItem(contextData);
          setProposedType("context");
          setRobotExpression("excited");
        } else if (action.action === "validate_offer" || action.action === "validate_persona" || action.action === "validate_criterion" || action.action === "validate_context") {
          // Item was validated by the user in previous turn
        } else if (action.action === "offers_complete" || action.action === "personas_complete" || action.action === "criteria_complete" || action.action === "contexts_complete") {
          onComplete();
        }
      }
    } catch (err: any) {
      setMessages([...newMessages, { role: "assistant", content: `Désolé, une erreur est survenue. ${err.message}` }]);
      setRobotExpression("happy");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validation d'une proposition de l'assistant.
   *
   * L'appel n'était pas attendu (`onItemCreated` sans `await`) et les appelants
   * l'enveloppaient d'un `catch {}` vide : quand l'enregistrement échouait,
   * l'élément disparaissait de l'écran sans un mot, et l'utilisateur croyait
   * avoir perdu sa saisie. Désormais la proposition est CONSERVÉE en cas
   * d'échec — il peut réessayer sans tout recommencer.
   */
  const handleValidateItem = async () => {
    if (!proposedItem || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onItemCreated(proposedType, proposedItem);
      setProposedItem(null);
      // Send validation message to continue the conversation
      sendMessage("C'est validé, je suis satisfait(e) de cette version.");
    } catch (err) {
      console.error("[AiChat] enregistrement impossible:", err);
      setSaveError(t("aiChat.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = () => {
    setProposedItem(null);
    inputRef.current?.focus();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    sendMessage(input.trim());
  };

  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden" style={{ backgroundColor: bgColor }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ backgroundColor: robotColor + "15" }}>
        <RobotMascot color={robotColor} expression={robotExpression} size={48} />
        <div>
          <h2 className="font-bold text-lg" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((msg, i) => (
          <MessageBubble key={i} role={msg.role} content={msg.content} robotColor={robotColor} />
        ))}

        {/* Proposed item card */}
        {proposedItem && (
          <>
            <ProposedItemCard
              data={proposedItem}
              type={proposedType}
              onValidate={handleValidateItem}
              onEdit={handleEditItem}
            />
            {/* L'échec est dit là où l'utilisateur regarde, et la proposition
                reste à l'écran : rien n'est perdu, il peut réessayer. */}
            {saveError && (
              <div
                role="alert"
                className="rounded-xl border px-4 py-3 text-sm"
                style={{ background: "#FBE9E7", borderColor: "#F49390", color: "#C0423F" }}
              >
                {saveError}
              </div>
            )}
          </>
        )}

        {/* Thinking indicator */}
        {loading && (
          <div className="flex gap-3 items-center">
            <RobotMascot color={robotColor} expression="thinking" size={36} />
            <div className="flex gap-1.5 px-4 py-3 bg-white rounded-2xl border">
              <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-6 py-4 border-t bg-white">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ focusRingColor: robotColor } as any}
            placeholder={t("common.writeYourAnswer")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className="rounded-xl px-4"
            style={{ backgroundColor: robotColor }}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
